import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import type {
  BreakdownSubItem,
  BreakdownSubItemInput,
  EquipmentServiceRate,
  EquipmentType,
} from '@jobos/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { dateToLocalISO } from '@/lib/format';
import { AddUnitPopover, type AddUnitInput } from './AddUnitPopover';
import { AddVisitPopover, type AddVisitInput } from './AddVisitPopover';
import { MoneyInput } from './MoneyInput';
import { UnitTypeGroup } from './UnitTypeGroup';
import {
  buildSubItemsFromUI,
  clampFlatDiscount,
  clampVisitDiscount,
  computeBillTotals,
  groupUnitsByType,
  readUIFromSubItems,
  type EquipmentBillDiscountUI,
  type EquipmentFlatUI,
  type EquipmentUIState,
  type EquipmentUnitUI,
  type EquipmentVisitUI,
} from './equipment-helpers';

const money = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

let clientIdSeq = 0;
const nextClientId = (kind: string) => `${kind}-${Date.now()}-${++clientIdSeq}`;

export interface EquipmentEditorProps {
  equipmentTypes: EquipmentType[];
  serviceRates: EquipmentServiceRate[];
  initialSubItems: BreakdownSubItem[];
  onChange: (next: BreakdownSubItemInput[]) => void;
  footer?: ReactNode;
  readOnly?: boolean;
  lineItemDescription?: string | null;
}

export function EquipmentEditor({
  equipmentTypes,
  serviceRates,
  initialSubItems,
  onChange,
  footer,
  readOnly = false,
  lineItemDescription = null,
}: EquipmentEditorProps) {
  const [state, setState] = useState<EquipmentUIState>(() =>
    readUIFromSubItems(initialSubItems, equipmentTypes, serviceRates),
  );

  const initialKey = useMemo(
    () =>
      JSON.stringify(
        initialSubItems.map((s) => [
          s.id,
          s.type,
          (s as { amount?: number | null }).amount ?? null,
          (s as { note?: string | null }).note ?? null,
        ]),
      ),
    [initialSubItems],
  );
  useEffect(() => {
    setState(readUIFromSubItems(initialSubItems, equipmentTypes, serviceRates));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey, equipmentTypes, serviceRates]);

  const totals = useMemo(
    () => computeBillTotals(state, equipmentTypes, serviceRates),
    [state, equipmentTypes, serviceRates],
  );

  const emit = (next: EquipmentUIState) => {
    if (readOnly) return;
    setState(next);
    onChange(buildSubItemsFromUI(next, equipmentTypes, serviceRates));
  };

  const handleAddUnit = (input: AddUnitInput) => {
    const newUnit: EquipmentUnitUI = {
      id: nextClientId('u'),
      typeKey: input.typeKey,
      custom: input.custom,
      assetTag: input.assetTag,
      location: input.location,
      startDate: input.startDate,
      endDate: input.endDate,
      days: input.days,
    };
    emit({ ...state, units: [...state.units, newUnit] });
  };

  const handleUpdateUnit = (unitId: string, patch: Partial<EquipmentUnitUI>) => {
    emit({
      ...state,
      units: state.units.map((u) => (u.id === unitId ? { ...u, ...patch } : u)),
    });
  };

  const handleRemoveUnit = (unitId: string) => {
    emit({ ...state, units: state.units.filter((u) => u.id !== unitId) });
  };

  const handleAddVisit = (input: AddVisitInput) => {
    if (input.editingId) {
      emit({
        ...state,
        visits: state.visits.map((v) =>
          v.id === input.editingId
            ? { ...v, rateKey: input.rateKey, dates: input.dates, qty: input.qty }
            : v,
        ),
      });
      return;
    }
    const newVisit: EquipmentVisitUI = {
      id: nextClientId('v'),
      rateKey: input.rateKey,
      dates: input.dates,
      qty: input.qty,
    };
    emit({ ...state, visits: [...state.visits, newVisit] });
  };

  const handleUpdateVisit = (visitId: string, patch: Partial<EquipmentVisitUI>) => {
    emit({
      ...state,
      visits: state.visits.map((v) => (v.id === visitId ? { ...v, ...patch } : v)),
    });
  };

  const handleRemoveVisit = (visitId: string) => {
    emit({ ...state, visits: state.visits.filter((v) => v.id !== visitId) });
  };

  const handleAddFlat = () => {
    const newFlat: EquipmentFlatUI = {
      id: nextClientId('f'),
      description: '',
      amount: 0,
    };
    emit({ ...state, flats: [...state.flats, newFlat] });
  };

  const handleUpdateFlat = (flatId: string, patch: Partial<EquipmentFlatUI>) => {
    emit({
      ...state,
      flats: state.flats.map((f) => (f.id === flatId ? { ...f, ...patch } : f)),
    });
  };

  const handleRemoveFlat = (flatId: string) => {
    emit({ ...state, flats: state.flats.filter((f) => f.id !== flatId) });
  };

  const handleAddBillDiscount = () => {
    const newDiscount: EquipmentBillDiscountUI = {
      id: nextClientId('bd'),
      label: '',
      amount: 0,
    };
    emit({ ...state, billDiscounts: [...state.billDiscounts, newDiscount] });
  };

  const handleUpdateBillDiscount = (
    discountId: string,
    patch: Partial<EquipmentBillDiscountUI>,
  ) => {
    emit({
      ...state,
      billDiscounts: state.billDiscounts.map((d) =>
        d.id === discountId ? { ...d, ...patch } : d,
      ),
    });
  };

  const handleRemoveBillDiscount = (discountId: string) => {
    emit({
      ...state,
      billDiscounts: state.billDiscounts.filter((d) => d.id !== discountId),
    });
  };

  const inUseByType = useMemo(() => {
    const map: Record<string, { assetTag: string; location: string | null }[]> = {};
    for (const u of state.units) {
      if (!map[u.typeKey]) map[u.typeKey] = [];
      map[u.typeKey]!.push({ assetTag: u.assetTag, location: u.location });
    }
    return map;
  }, [state.units]);

  const ratesByKey = useMemo(
    () => new Map(serviceRates.map((r) => [r.key, r])),
    [serviceRates],
  );

  const groups = useMemo(
    () => groupUnitsByType(state.units, equipmentTypes),
    [state.units, equipmentTypes],
  );

  // All three section tables share the same trailing column widths
  // (Discount / Total / remove), so those columns line up down the whole
  // editor instead of staggering per section.
  return (
    <div data-testid="equipment-editor" className="space-y-6">
      {/* Services group on top */}
      <section data-testid="equipment-services-group" className="border-b border-gray-200 pb-5">
        <header className="flex items-baseline gap-3 pb-2 border-b border-gray-100">
          <span className="font-semibold text-sm text-gray-900">Service calls &amp; visits</span>
          <span className="ml-auto font-semibold text-sm tabular-nums text-gray-900 pr-11">
            {money(totals.visitsTotal)}
          </span>
        </header>
        {state.visits.length === 0 ? (
          <p className="text-xs text-gray-400 py-3">No service calls yet.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed text-sm mt-1">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                <th className="text-left py-1.5 pr-2 font-medium">Type</th>
                <th className="text-left py-1.5 pr-2 font-medium w-[190px]">Date(s)</th>
                <th className="text-right py-1.5 pr-2 font-medium w-[90px]">Rate</th>
                <th className="text-right py-1.5 pr-2 font-medium w-[110px]">Discount</th>
                <th className="text-right py-1.5 pr-2 font-medium w-[110px]">Total</th>
                <th className="w-9"></th>
              </tr>
            </thead>
            <tbody>
              {state.visits.map((v) => {
                const rate = ratesByKey.get(v.rateKey);
                const rateAmount = Number(rate?.rate ?? 0);
                const discount = rate ? clampVisitDiscount(v, rate) : 0;
                const lineTotal = v.qty * rateAmount - discount;
                return (
                  <tr key={v.id} className="border-t border-gray-50 group">
                    <td className="py-1.5 pr-2 text-gray-900">{rate?.label ?? v.rateKey}</td>
                    <td className="py-1.5 pr-2">
                      <div className="w-[164px]">
                      <DatePicker
                        aria-label={`Date for ${rate?.label ?? v.rateKey}`}
                        value={v.dates[0] ?? null}
                        disabled={readOnly}
                        placeholder="Optional"
                        onChange={(d) =>
                          handleUpdateVisit(v.id, {
                            dates: d ? [dateToLocalISO(d)] : [],
                            qty: 1,
                          })
                        }
                        className="h-7 text-sm pr-16"
                      />
                      </div>
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-gray-700">
                      {money(rateAmount)}
                    </td>
                    <td className="py-1.5 pr-2 text-right">
                      <MoneyInput
                        mode="currency"
                        ariaLabel={`Discount for ${rate?.label ?? v.rateKey}`}
                        value={v.discount ?? 0}
                        disabled={readOnly}
                        onChange={(val) =>
                          handleUpdateVisit(v.id, { discount: val > 0 ? val : null })
                        }
                        className="w-20 ml-auto"
                      />
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums font-medium text-gray-900">
                      {money(lineTotal)}
                    </td>
                    <td className="py-1.5 w-9 text-center">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => handleRemoveVisit(v.id)}
                          aria-label="Remove visit"
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
        {!readOnly && (
          <div className="mt-2">
            <AddVisitPopover rates={serviceRates} onAdd={handleAddVisit} />
          </div>
        )}
      </section>

      {/* Equipment groups */}
      <section data-testid="equipment-groups">
        <header className="flex items-baseline gap-3 pb-2 border-b border-gray-100">
          <span className="font-semibold text-sm text-gray-900">Equipment</span>
          <span className="ml-auto font-semibold text-sm tabular-nums text-gray-900 pr-11">
            {money(totals.unitsTotal)}
          </span>
        </header>
        {groups.map((g) => (
          <UnitTypeGroup
            key={g.type.key}
            type={g.type}
            units={g.units}
            onUpdate={handleUpdateUnit}
            onRemove={handleRemoveUnit}
            readOnly={readOnly}
          />
        ))}
        {groups.length === 0 && (
          <p className="text-xs text-gray-400 py-3">No equipment deployed yet.</p>
        )}
        {!readOnly && (
          <div className="mt-2">
            <AddUnitPopover types={equipmentTypes} inUseByType={inUseByType} onAdd={handleAddUnit} />
          </div>
        )}
      </section>

      {/* Other charges (legacy + freeform) */}
      <section
        data-testid="equipment-flats-group"
        className="border-t border-gray-200 pt-5"
      >
        <header className="flex items-baseline gap-3 pb-2 border-b border-gray-100">
          <span className="font-semibold text-sm text-gray-900">Other charges</span>
          <span className="ml-auto font-semibold text-sm tabular-nums text-gray-900 pr-11">
            {money(totals.flatsTotal)}
          </span>
        </header>
        {state.flats.length === 0 ? (
          <p className="text-xs text-gray-400 py-3">No other charges.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed text-sm mt-1">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                <th className="text-left py-1.5 pr-2 font-medium">Description</th>
                <th className="text-right py-1.5 pr-2 font-medium w-[110px]">Amount</th>
                <th className="text-right py-1.5 pr-2 font-medium w-[110px]">Discount</th>
                <th className="text-right py-1.5 pr-2 font-medium w-[110px]">Total</th>
                <th className="w-9" />
              </tr>
            </thead>
            <tbody>
              {state.flats.map((f) => {
                return (
                  <tr key={f.id} className="border-t border-gray-50 group">
                    <td className="py-1.5 pr-2">
                      <Input
                        type="text"
                        aria-label="description"
                        placeholder={lineItemDescription ?? 'Flat charge'}
                        value={f.description}
                        disabled={readOnly}
                        onChange={(e) => handleUpdateFlat(f.id, { description: e.target.value })}
                        className="h-7 text-sm max-w-[420px]"
                      />
                    </td>
                    <td className="py-1.5 pr-2 text-right">
                      <MoneyInput
                        mode="integer"
                        ariaLabel="amount"
                        value={f.amount}
                        disabled={readOnly}
                        onChange={(val) => handleUpdateFlat(f.id, { amount: val })}
                        className="w-20 ml-auto"
                      />
                    </td>
                    <td className="py-1.5 pr-2 text-right">
                      <MoneyInput
                        mode="currency"
                        ariaLabel={`Discount for ${f.description || 'other charge'}`}
                        value={f.discount ?? 0}
                        disabled={readOnly}
                        onChange={(val) =>
                          handleUpdateFlat(f.id, { discount: val > 0 ? val : null })
                        }
                        className="w-20 ml-auto"
                      />
                    </td>
                    <td className="py-1.5 pr-2 text-right tabular-nums font-medium text-gray-900">
                      {money(f.amount - clampFlatDiscount(f))}
                    </td>
                    <td className="py-1.5 w-9 text-center">
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFlat(f.id)}
                          aria-label="Remove charge"
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
        {!readOnly && (
          <div className="mt-2 -ml-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAddFlat}
              className="h-8 text-sm text-emerald-700 hover:text-emerald-800"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add charge
            </Button>
          </div>
        )}
      </section>

      {/* Totals footer */}
      <footer
        data-testid="equipment-totals"
        className="border-t-2 border-gray-200 pt-4 mt-5 space-y-2"
      >
        <div className="flex justify-between text-sm text-gray-700">
          <span>Subtotal</span>
          <span className="tabular-nums pr-11">{money(totals.subtotal)}</span>
        </div>
        {(state.billDiscounts.length > 0 || !readOnly) && (
          <div data-testid="equipment-bill-discounts" className="pt-2 space-y-1">
            <div className="text-sm text-gray-700">Bill discounts</div>
            {state.billDiscounts.length === 0 ? (
              <p className="text-xs text-gray-400 py-1">No bill discounts.</p>
            ) : (
              <table className="w-full text-sm mt-1">
                <tbody>
                  {state.billDiscounts.map((d, i) => (
                    <tr key={d.id} className="group">
                      <td className="py-1 pr-2">
                        <Input
                          type="text"
                          aria-label={`Discount description ${i + 1}`}
                          placeholder="Description"
                          value={d.label}
                          disabled={readOnly}
                          onChange={(e) =>
                            handleUpdateBillDiscount(d.id, { label: e.target.value })
                          }
                          className="h-7 text-sm max-w-[420px]"
                        />
                      </td>
                      <td className="py-1 pr-2 text-right w-[110px]">
                        <MoneyInput
                          mode="currency"
                          ariaLabel={`Discount amount ${i + 1}`}
                          value={d.amount}
                          disabled={readOnly}
                          onChange={(val) => handleUpdateBillDiscount(d.id, { amount: val })}
                          className="w-20 ml-auto"
                        />
                      </td>
                      <td className="py-1 w-9 text-center">
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveBillDiscount(d.id)}
                            aria-label={`Remove discount ${i + 1}`}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!readOnly && (
              <div className="pt-1 -ml-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleAddBillDiscount}
                  className="h-8 text-sm text-emerald-700 hover:text-emerald-800"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add discount
                </Button>
              </div>
            )}
          </div>
        )}
        {totals.discount > 0 && (
          <div className="flex justify-between text-sm text-gray-600 pt-1">
            <span>Total discounts</span>
            <span className="tabular-nums pr-11">−{money(totals.discount)}</span>
          </div>
        )}
        <div className="flex justify-between text-base font-semibold text-gray-900 border-t pt-3 mt-1">
          <span>Total</span>
          <span className="tabular-nums pr-11">{money(totals.grandTotal)}</span>
        </div>
        {footer && <div className="pt-2">{footer}</div>}
      </footer>
    </div>
  );
}
